import { useLocalization } from "@fluent/react";
import { useState } from "react";
import type Account from "byzantine/src/Account";
import { useLoanPaymentEnabled } from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import BaseBanner from "../BaseBanner";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import EditAccountNameModal from "./EditAccountNameModal";

interface EditIconProps {
  onClick: () => void;
}

const EditIcon = ({ onClick }: EditIconProps) => {
  const { l10n } = useLocalization();

  return (
    <div
      className="margin--left--m fontSize--l"
      style={{ transform: "translateY(0.1em)" }}
    >
      <button
        className="button--reset narmi-icon-edit clickable"
        onClick={onClick}
        onKeyUp={({ key }) => {
          if (key === "Enter") onClick();
        }}
        aria-label={l10n.getString("aria-label-edit-name")}
        role="button"
        tabIndex={0}
      />
    </div>
  );
};

interface AccountBannerProps {
  account: Account;
}
const AccountBanner = ({ account }: AccountBannerProps) => {
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isWebLoanPaymentsEnabled } = useLoanPaymentEnabled();

  const userFeatures = useUserFeatures();

  const buttonName =
    account.isInternalLoan() &&
    account.isValidInternalOrExternalTransferDestination(
      Boolean(institution.ach_allows_push)
    )
      ? l10n.getString("button-make-a-payment")
      : "";
  const isEligibleForLoanPaymentByCard =
    account.isPayableByCard() && userFeatures?.loan_payment_by_card;
  const isEligibleForLoanPrincipalPayment =
    account.isInternalLoan() && userFeatures?.loan_principal_payment;

  const shouldUseNewLoanFlow =
    isWebLoanPaymentsEnabled && account.isInternalLoan();

  const shouldUseLegacyLoanFlow =
    !isWebLoanPaymentsEnabled &&
    (isEligibleForLoanPaymentByCard || isEligibleForLoanPrincipalPayment);

  const shouldUseLoanFlow = shouldUseNewLoanFlow || shouldUseLegacyLoanFlow;
  const urlBase = shouldUseLoanFlow ? "loans" : "funds";

  return (
    <div>
      <BaseBanner
        bannerName={account.nickname || account.name}
        buttonFunction={() =>
          window.location.assign(
            `/transfer/${urlBase}/?to_account_id=${account.id}&previous=/accounts/${account.id}`
          )
        }
        buttonName={buttonName || ""}
        orFeatureFlagChecks={["internal_transfers", "ach"]}
        features={userFeatures}
        icon={
          <EditIcon
            onClick={() => {
              setDialogOpen(true);
            }}
          />
        }
      />
      <EditAccountNameModal
        name={account.nickname || account.name}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        accountUuid={account.id}
      />
    </div>
  );
};
export default AccountBanner;
